import produce from "immer";
import { createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { Amenity } from "../../../Types/UnitFeatureTypes";


export interface FetchAmenityInfoState {
  request: {
      loading: boolean;
      error: string | null;
      data: Amenity[] | null;
      blockedSlots: string[] | null;
      count: string | null;
  };
}

export enum Types {
  FETCH_AMENITY_LIST_REQUEST = "FETCH_AMENITY_LIST_REQUEST",
  FETCH_AMENITY_LIST_SUCCESS = "FETCH_AMENITY_LIST_SUCCESS",
  FETCH_AMENITY_LIST_FAILURE = "FETCH_AMENITY_LIST_FAILURE",
  FETCH_BLOCKED_SLOT_REQUEST = "FETCH_BLOCKED_SLOT_REQUEST",
  FETCH_BLOCKED_SLOT_SUCCESS = "FETCH_BLOCKED_SLOT_SUCCESS",
  FETCH_BLOCKED_SLOT_FAILURE = "FETCH_BLOCKED_SLOT_FAILURE",
}

const blockedSlotActions = createAsyncAction(
  Types.FETCH_BLOCKED_SLOT_REQUEST,
  Types.FETCH_BLOCKED_SLOT_SUCCESS,
  Types.FETCH_BLOCKED_SLOT_FAILURE,
)
<
  { amenity_id: number, start_date: string },
  { slots: string[] },
  { message: string }
>();

const amenityActions = createAsyncAction(
  Types.FETCH_AMENITY_LIST_REQUEST,
  Types.FETCH_AMENITY_LIST_SUCCESS,
  Types.FETCH_AMENITY_LIST_FAILURE,
)
<
  { id?: string, offset?: number },
  { amenitiesList: Amenity[], count: string },
  { message: string }
>();

export const Actions = { amenityActions, blockedSlotActions };

export const initialState: FetchAmenityInfoState = {
  request: {
    loading: false,
    error: null as string | null,
    data: null,
    blockedSlots: null,
    count: '0'
  }
};

export type FetchAmenitiesListState = typeof initialState;

const fetchAmenitiesListRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.data = null;

    return draft;
  });

const fetchAmenitiesListSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.amenityActions.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.data = payload.amenitiesList;
    draft.request.count = payload.count;
    if (draft.request.data) {
      draft.request.loading = false;
    }

    return draft;
  });

const fetchAmenitiesListFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.amenityActions.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

const fetchBlockedSlotRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.error = null;
    draft.request.blockedSlots = null;

    return draft;
  });

const fetchBlockedSlotSuccess = (
  state = initialState,
  { payload }: ActionType<typeof blockedSlotActions.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.blockedSlots = payload.slots;
    if (draft.request.blockedSlots) {
      draft.request.loading = false;
    }

    return draft;
  });

const fetchBlockedSlotFailure = (
  state = initialState,
  { payload }: ActionType<typeof blockedSlotActions.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.error = payload.message;

    return draft;
  });

export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.amenityActions.request, fetchAmenitiesListRequest)
  .handleAction(Actions.amenityActions.success, fetchAmenitiesListSuccess)
  .handleAction(Actions.amenityActions.failure, fetchAmenitiesListFailure)
  .handleAction(Actions.blockedSlotActions.request, fetchBlockedSlotRequest)
  .handleAction(Actions.blockedSlotActions.success, fetchBlockedSlotSuccess)
  .handleAction(Actions.blockedSlotActions.failure, fetchBlockedSlotFailure);
  